import { getFallbackLocale } from "@/i18n";
import { Countries } from "@/types/Countries";
import { IPayOptionsState } from "@/types/IPayOptionsState";
import { ComposerTranslation, useI18n } from "vue-i18n";
import { useStore } from "vuex";

export type Ilocations = "pt_BR" | "es_CO" | "es_EC" | "es_MX" | "es_PA" | "es_PE" | "en_US" | "en_ZA";

/**
 * Locales supported by the extension UI: all PayOptions locales plus en-ZA and es-PA.
 */
export const EXTENSION_LOCALES: ReadonlySet<string> = new Set([
  "en", "en-PH", "en-ZA",
  "es", "es-CO", "es-MX", "es-PE", "es-PA",
  "fil", "ceb-PH", "fil-PH", "tl-PH",
  "pt-BR",
  "id",
]);

/**
 * Resolves a locale string against the locales supported by the extension UI.
 *
 * Resolution order:
 * 1. Exact match (case-insensitive).
 * 2. First supported locale sharing the same language subtag (e.g. `es-AR` → `es-MX`).
 * 3. Fallback: `"en"`.
 *
 * When `lang` is omitted the browser locale (`window.navigator.language`) is used as input.
 *
 * @param lang - BCP 47 locale tag from the `?lang=` query param, or `undefined`.
 * @returns A locale key present in {@link EXTENSION_LOCALES}.
 */
export const resolveExtensionLocale = (lang?: string): string => {
  const input = (lang ?? window.navigator.language).trim();

  const exactMatch = [...EXTENSION_LOCALES].find(
    (l) => l.toLowerCase() === input.toLowerCase()
  );
  if (exactMatch) return exactMatch;

  const langCode = input.split("-")[0].toLowerCase();
  const langMatch = [...EXTENSION_LOCALES].find(
    (l) => l.split("-")[0].toLowerCase() === langCode
  );
  if (langMatch) return langMatch;

  return "en";
};

const CURRENT_LANG =
  typeof localStorage?.getItem === "function" ? localStorage?.getItem("lang") || window.navigator.language : "es_MX";

/**
 * To get the language from the local storage, example "en_US"
 * @returns string
 */
export const getLanguage = (): string => CURRENT_LANG;

/**
 * To get just the language, example "en"
 * @returns string
 */
export const getLanguageName = (): string => CURRENT_LANG.substring(0, 2);

/**
 * To get just the location name, example: "US" || "us"
 * @param lowerCase optional boolean default false
 * @returns string
 */
export const getLocationName = (lowerCase?: boolean): string => {
  const locationName = CURRENT_LANG.substring(3, 5);
  return lowerCase ? locationName.toLowerCase() : locationName;
};

const mapLanguageLocale = {
  es: "es_MX",
  en: "en_US",
  pt: "pt_BR",
};

export const getDefaultLocale = (): string => mapLanguageLocale[getLanguageName().toLowerCase()];

export const getSupportedLanguage = (lang: string): string => {
  lang = lang.toLowerCase();
  const langLocale = lang.substring(0, 2);

  if (langLocale === "en") {
    return "en_US";
  }

  if (langLocale === "es") {
    switch (lang) {
      case "es-co":
        return "es_CO";
      case "es-ec":
        return "es_EC";
      case "es-mx":
        return "es_MX";
      case "es-pe":
        return "es_PE";
      case "es-pa":
        return "es_PA";
      default:
        return "es_MX";
    }
  }

  if (langLocale === "pt") {
    return "pt_BR";
  }

  return "";
};

/**
 * Computes the term label for a installment detail.
 * Example: it receives for term: "week8" and returns "8 weeks" or "8 semanas", depending on the locale.
 * Example: if it receives "week", returns "1 week".
 *
 * @param t - Function to translate messages depending on the locale.
 * @param te - Function that checks if a translation key exists.
 * @param term - The installment term to be translated.
 * @returns string - The computed label phrase.
 */
export const computeTermLabel = (t: ComposerTranslation, te: (key: string) => boolean, term: string): string => {
  if (te(term) || t(term) !== term) {
    // TODO: Check why `te` is not working as expected
    // if the translation key is defined for the locale
    return t(term);
  } else if (term === "week") {
    return `1 ${t("week")}`;
  } else if (term.includes("week")) {
    // its a longer weekly period
    const totalWeeks = parseInt(term.replace("week", ""));
    if (!isNaN(totalWeeks)) return `${totalWeeks} ${t("weeks")}`;
  }

  return term;
};

/**
 * Computes the amount value formatted as currency based on the device country or locale.
 * @param amount - The amount to be formatted.
 * @param roundToWhole - When true, rounds the amount to the nearest whole unit before
 * formatting so no decimals are ever shown, regardless of the locale's currency format
 * (e.g. the Super Pacman installment screen, which always displays whole numbers).
 * @returns The formatted currency string.
 */
export const computedAmountValue = (amount: number, roundToWhole = false): string => {
  const { n, locale } = useI18n();
  const store = useStore<{ payOptions: IPayOptionsState }>();

  const response = n(
    roundToWhole ? Math.round(amount) : amount,
    "currency",
    getFallbackLocale(store.state.payOptions.deviceInfo.country ?? locale.value),
  );
  return response.replace(/\u00A0/g, ""); // Remove non-breaking space if present
};

/**
 * Translates days paid to a label for display.
 * @param daysPaid - The number of days paid.
 * @param isMinimumPayment - Boolean indicating if it's a minimum payment.
 * @param t - Function to translate messages depending on the locale.
 * @returns An array containing the original label and the translated label.
 */
export const translateDaysPaidToLabel = (
  daysPaid: number,
  isMinimumPayment: boolean,
  t: (key: string) => string
): string[] => {
  let label = "";
  let originalLabel = "";
  let subtitle = "";
  // Using if instead of switch because if statements are faster and less costly
  if (daysPaid < 7) {
    originalLabel = "week";
    let dateLabel = t("days");
    if (daysPaid == 1) {
      dateLabel = t("day");
    }
    label = `${daysPaid} ${dateLabel}`;
  } else if (daysPaid === 7) {
    originalLabel = "week";
    label = `${t("week")}`;
  } else if (daysPaid === 14) {
    originalLabel = "week2";
    label = `${t("week2")}`;
  } else if (daysPaid === 21) {
    originalLabel = "week3";
    label = `${t("week3")}`;
  } else if (daysPaid >= 28) {
    originalLabel = "month";
    label = `${t("month")}`;
  }

  if (isMinimumPayment) {
    subtitle = t("minimum-payment");
  }
  return [originalLabel, label, subtitle];
};

// DO NOT USE THIS FUNCTION IN M3!!!
export const getLocaleFromCountry = (country: string): string => {
  switch (country) {
    case Countries.BRAZIL:
      return "pt_BR";
    case Countries.COLOMBIA:
      return "es_CO";
    case Countries.ECUADOR:
      return "es_EC";
    case Countries.MEXICO:
      return "es_MX";
    case Countries.PANAMA:
      return "es_PA";
    case Countries.PERU:
      return "es_PE";
    case Countries.PHILIPPINES:
      return "en_PH";
    case "US":
    case Countries.SOUTH_AFRICA:
      return "en_US";
    default:
      return "es_MX";
  }
};
